import React, { useState } from 'react';
import { SECTIONS, DB } from '../data/ingredients';
import { AddIngredientModal } from './IngredientDatabase';
import { pctToGrams, gramsToPct, getStatus, calcPPM, isPPMSafe } from '../utils/calculations';
import styles from './IngredientGrid.module.css';

export default function IngredientGrid({ calc, ingDB }) {
  const { rows, updateRow, addRow, removeRow, meatWeightG, totals, casingResult } = calc;

  return (
    <div className={styles.grid}>
      {SECTIONS.map(sect => {
        const sectRows = rows.filter(r => r.sectionKey === sect.key);
        return (
          <Section
            key={sect.key}
            sect={sect}
            rows={sectRows}
            meatWeightG={meatWeightG}
            totals={totals}
            allRows={rows}
            ingDB={ingDB}
            casingResult={casingResult}
            onUpdate={updateRow}
            onAdd={() => addRow(sect.key)}
            onRemove={removeRow}
            onAutoFill={(id) => {
              // Find the current total of all OTHER meat+fat rows
              const otherMF = rows
                .filter(r => r.id !== id && (r.sectionKey === 'meat' || r.sectionKey === 'fat') && r.pct)
                .reduce((sum, r) => sum + (parseFloat(r.pct) || 0), 0);
              const needed = Math.max(0, Math.round((100 - otherMF) * 1000) / 1000);
              updateRow(id, 'pct', needed.toString());
            }}
          />
        );
      })}
    </div>
  );
}

function Section({ sect, rows, meatWeightG, totals, allRows, ingDB, casingResult, onUpdate, onAdd, onRemove, onAutoFill }) {
  const [collapsed, setCollapsed] = useState(true);

  // Casings are measured in feet, so their section summary counts length, not %
  const isCasing = Boolean(sect.isCasingSection);

  // Section-level summary stats
  const filledRows = isCasing
    ? rows.filter(r => r.name && parseFloat(r.feet) > 0)
    : rows.filter(r => r.name && r.pct);
  const sectTotal  = isCasing
    ? filledRows.reduce((s, r) => s + (parseFloat(r.feet) || 0), 0)
    : filledRows.reduce((s, r) => s + (parseFloat(r.pct) || 0), 0);
  const hasFilled  = filledRows.length > 0;

  // Special sub-totals and warnings
  const isMeat   = sect.key === 'meat' || sect.key === 'fat';
  const mfStatus = isMeat && totals
    ? Math.abs(totals.mfPct - 100) < 0.01 ? 'good'
    : totals.mfPct > 100 ? 'danger' : 'warn'
    : null;

  return (
    <div
      className={styles.section}
      style={{ '--sect-color': sect.color }}
    >
      {/* Section header */}
      <div className={styles.sectionHeader} onClick={() => setCollapsed(c => !c)}>
        <div className={styles.sectionLeft}>
          <div className={styles.sectionDot} />
          <span className={styles.sectionName}>{sect.label}</span>
          {hasFilled && (
            isCasing ? (
              <span className={styles.sectionTotal}>
                {sectTotal.toFixed(1)} ft
                <span className={styles.sectionGrams}>
                  {' '}·{' '}not counted in formula weight
                </span>
              </span>
            ) : (
              <span className={styles.sectionTotal}>
                {sectTotal.toFixed(2)}%
                {meatWeightG > 0 && (
                  <span className={styles.sectionGrams}>
                    {' '}·{' '}{pctToGrams(sectTotal, meatWeightG).toFixed(0)}g
                  </span>
                )}
              </span>
            )
          )}
        </div>
        <div className={styles.sectionRight}>
          
          <span className={styles.collapseIcon}>{collapsed ? '›' : '⌄'}</span>
        </div>
      </div>

      {/* Column headers (shown once per section, not per row) */}
      {!collapsed && (
        <>
          <div className={styles.colHeaders}>
            <span className={styles.colIngredient}>{isCasing ? 'Casing' : 'Ingredient'}</span>
            <span className={styles.colPct}>{isCasing ? 'Feet' : '%'}</span>
            <span className={styles.colGrams}>{isCasing ? 'Cost' : 'Grams'}</span>
            <span className={styles.colStatus}>{isCasing ? '' : 'Status'}</span>
            <span className={styles.colNote}>Note</span>
            <span className={styles.colAction} />
          </div>

          {/* Ingredient rows */}
          <div className={styles.rows}>
            {rows.map((row, idx) => (
              <IngredientRow
                key={row.id}
                row={row}
                sect={sect}
                meatWeightG={meatWeightG}
                isAlt={idx % 2 === 1}
                canRemove={rows.length > 1}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onAutoFill={onAutoFill}
                ingDB={ingDB}
                casingResult={casingResult}
              />
            ))}
          </div>

          {/* Add row button */}
          <button className={styles.addRow} onClick={onAdd}>
            {isCasing ? '+ Add casing' : '+ Add ingredient'}
          </button>

          {/* Casings hold the mix, they aren't part of it */}
          {isCasing && (
            <div className={styles.sectionFooter}>
              Casing length is priced per foot and excluded from formula and
              stuffable weight. Set $/yd or $/ft in 🗂️ Ingredients.
            </div>
          )}

          {/* Liquid warning */}
          {sect.key === 'liquid' && totals && totals.binderPct > 0 && totals.liquidPct < 10 && (
            <div className={styles.sectionWarning}>
              ⚠️ Binders detected — liquid should be ≥ 10%
              (currently {totals.liquidPct.toFixed(2)}%)
            </div>
          )}

          {/* Spice total */}
          {sect.key === 'spice' && hasFilled && (
            <div className={`${styles.sectionFooter} ${
              sectTotal > 2 ? styles.warn : sectTotal >= 0.5 ? styles.good : styles.low
            }`}>
              Spice total: {sectTotal.toFixed(3)}%
              {' · '}
              {sectTotal > 2 ? '⚠️ Over 2%' : sectTotal >= 0.5 ? '✓ In range' : '↓ Low'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IngredientRow({ row, sect, meatWeightG, isAlt, canRemove, onUpdate, onRemove, onAutoFill, ingDB, casingResult }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [inputValue,   setInputValue]   = useState(row.name);

  // Keep local input value in sync when row.name changes externally (e.g. import)
  React.useEffect(() => { setInputValue(row.name); }, [row.name]);

  // Use live DB names, merged with static DB as fallback — ensures dropdown is never empty
  const liveNames   = ingDB ? ingDB.getNamesForSection(sect.key) : [];
  const dbEntries   = DB[sect.key] || [];
  const staticNames = dbEntries.map(e => e[0]);
  // Merge: live DB first, then any static names not already in live DB
  const names = liveNames.length > 0
    ? [...new Set([...liveNames, ...staticNames])]
    : staticNames;

  const pct    = parseFloat(row.pct) || 0;
  const grams  = meatWeightG > 0 && pct > 0 ? pctToGrams(pct, meatWeightG) : 0;

  const dbEntry = dbEntries.find(e => e[0] === row.name);
  const status  = getStatus(row.name, pct, dbEntry);

  // Typical range string for tooltip (issue 4)
  const rangeStr = dbEntry
    ? `Typical: ${dbEntry[1]}–${dbEntry[2]}%   Max: ${dbEntry[3]}%${dbEntry[4] ? '   ' + dbEntry[4] : ''}`
    : '';

  // For cure: compute PPM. The badge appears as soon as an amount has been
  // typed, not only when it parses above zero — otherwise a negative or
  // garbled entry showed no badge at all and the cook got no warning.
  const isCureRow = sect.isCure && row.name && String(row.pct).trim() !== '';
  // Pass the raw entry, so "abc" reaches calcPPM as NaN and comes back null
  // rather than being floored to a reassuring 0 ppm.
  const ppm       = isCureRow ? calcPPM(row.pct, row.name) : null;
  // isPPMSafe rejects a null: a cure row with no usable figure is never "safe".
  const ppmSafe   = isCureRow ? isPPMSafe(ppm) : true;

  // Grams input: typing grams back-calculates pct
  const handleGramsChange = (val) => {
    const g = parseFloat(val) || 0;
    if (meatWeightG > 0 && g > 0) {
      onUpdate(row.id, 'pct', gramsToPct(g, meatWeightG).toString());
    } else if (val === '' || val === '0') {
      onUpdate(row.id, 'pct', '');
    }
  };

  // Autofill button: shown only for meat/fat rows (issue 8)
  const canAutoFill = (sect.key === 'meat' || sect.key === 'fat') && row.name;

  // ── Casing rows: a length in feet, priced per foot ───────────────────────
  if (sect.isCasingSection) {
    const feet     = parseFloat(row.feet) || 0;
    const ingObj   = ingDB ? ingDB.getIngredient('casing', row.name) : null;
    // Casings are sold by the yard or the foot — never by weight
    const perFoot  = ingObj && ingObj.costPerUnit > 0
      ? ingObj.costPerUnit / (ingObj.costUnit === 'yd' ? 3 : 1)
      : 0;
    const lineCost = perFoot * feet;
    // The calculator's +15% buffer is what you actually pull off the hank
    const suggested = casingResult ? casingResult.feetBuffer : 0;

    return (
      <div className={`${styles.row} ${isAlt ? styles.rowAlt : ''}`}>
        <div className={styles.cellIngredient}>
          <input
            className={styles.ingInput}
            type="text"
            list={`dl-${row.id}`}
            placeholder="Select a casing…"
            value={inputValue}
            onFocus={() => setInputValue('')}
            onBlur={() => { if (!inputValue.trim()) setInputValue(row.name); }}
            onChange={e => {
              const val = e.target.value;
              setInputValue(val);
              onUpdate(row.id, 'name', val);
            }}
          />
          <datalist id={`dl-${row.id}`}>
            {names.map(n => <option key={n} value={n} />)}
          </datalist>
          {row.name && perFoot === 0 && (
            <div className={styles.rangeHint}>no price set — 🗂️ Ingredients</div>
          )}
          {row.name && perFoot > 0 && (
            <div className={styles.rangeHint}>
              ${perFoot.toFixed(3)}/ft
              {ingObj.costUnit === 'yd' ? ` · $${ingObj.costPerUnit.toFixed(2)}/yd` : ''}
            </div>
          )}
        </div>

        {/* Feet */}
        <div className={styles.cellPct}>
          <input
            className={styles.numInput}
            type="text"
            inputMode="decimal"
            placeholder="—"
            value={row.feet ?? ''}
            onChange={e => onUpdate(row.id, 'feet', e.target.value)}
            title="Feet of casing used — take this from the Casing Calculator"
          />
          <span className={styles.numUnit}>ft</span>
        </div>

        {/* Line cost */}
        <div className={styles.cellGrams}>
          <span className={styles.casingCost}>
            {lineCost > 0 ? `$${lineCost.toFixed(2)}` : '—'}
          </span>
        </div>

        {/* Pull the calculator's estimate */}
        <div className={styles.cellStatus}>
          {suggested > 0 && (
            <button
              className={styles.autoFillBtn}
              onClick={() => onUpdate(row.id, 'feet', String(suggested))}
              title={`Use the Casing Calculator estimate (${suggested} ft, includes the 15% buffer)`}
            >
              ← {suggested} ft
            </button>
          )}
        </div>

        <div className={styles.cellNote}>
          <input
            className={styles.noteInput}
            type="text"
            placeholder="Note…"
            value={row.note || ''}
            onChange={e => onUpdate(row.id, 'note', e.target.value)}
          />
        </div>

        <div className={styles.cellAction}>
          {canRemove && (
            <button className={styles.removeBtn} onClick={() => onRemove(row.id)} title="Remove row">
              ✕
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.row} ${isAlt ? styles.rowAlt : ''}`}>
      {/* Ingredient name */}
      <div className={styles.cellIngredient}>
        <input
          className={styles.ingInput}
          type="text"
          list={`dl-${row.id}`}
          placeholder="Select or type ingredient…"
          value={inputValue}
          onFocus={() => {
            // Clear on focus so browser shows full datalist instead of filtered matches
            setInputValue('');
          }}
          onBlur={() => {
            // If nothing was selected/typed, restore the saved name
            if (!inputValue.trim()) {
              setInputValue(row.name);
            }
          }}
          onChange={e => {
            const val = e.target.value;
            setInputValue(val);
            if (val === '+ Add new ingredient…') {
              setShowAddModal(true);
              setInputValue('');
              onUpdate(row.id, 'name', '');
            } else if (names.includes(val) || val === '') {
              // Only commit to state when a valid option is selected or cleared
              onUpdate(row.id, 'name', val);
            } else {
              // User is typing — update state so status indicator reacts
              onUpdate(row.id, 'name', val);
            }
          }}
        />
        <datalist id={`dl-${row.id}`}>
          {names.map(n => <option key={n} value={n} />)}
          {ingDB && <option value="+ Add new ingredient…" />}
        </datalist>
        {showAddModal && ingDB && (
          <AddIngredientModal
            sectionKey={sect.key}
            sectionLabel={sect.label}
            onAdd={(data) => {
              const newIng = ingDB.addIngredient(sect.key, data);
              onUpdate(row.id, 'name', newIng.name);
              setShowAddModal(false);
            }}
            onClose={() => setShowAddModal(false)}
          />
        )}
        {/* Range hint shown below name when ingredient is selected (issue 4) */}
        {dbEntry && (
          <div className={styles.rangeHint}>
            {dbEntry[1]}–{dbEntry[2]}% typical · max {dbEntry[3]}%
          </div>
        )}
      </div>

      {/* % input — plain text, always editable (issue 1) */}
      <div className={styles.cellPct}>
        <input
          className={styles.numInput}
          type="text"
          inputMode="decimal"
          placeholder="—"
          value={row.pct || ''}
          onChange={e => onUpdate(row.id, 'pct', e.target.value)}
          title={rangeStr}
        />
        <span className={styles.numUnit}>%</span>
      </div>

      {/* Grams — editable, back-calculates % (issue 1 & 5) */}
      <div className={styles.cellGrams}>
        <input
          className={styles.numInput}
          type="text"
          inputMode="decimal"
          placeholder="—"
          value={grams > 0 ? grams.toFixed(1) : ''}
          onChange={e => handleGramsChange(e.target.value)}
          title="Enter grams to back-calculate %"
        />
        <span className={styles.numUnit}>g</span>
      </div>

      {/* Status + autofill */}
      <div className={styles.cellStatus}>
        {status && (
          <span className={`${styles.statusBadge} ${styles[status.type]}`}>
            {status.label}
          </span>
        )}
        {isCureRow && (
          <span className={`${styles.ppmBadge} ${ppmSafe ? styles.ppmSafe : styles.ppmDanger}`}>
            {ppm === null ? 'check cure amount' : `${ppm} ppm`}
          </span>
        )}
        {/* Autofill to 100% button — meat/fat rows only (issue 8) */}
        {canAutoFill && (
          <button
            className={styles.autoFillBtn}
            onClick={() => onAutoFill(row.id)}
            title="Auto-fill: set this % so Meat+Fat totals exactly 100%"
          >
            →100%
          </button>
        )}
      </div>

      {/* Note input — tooltip shows DB info when empty (issue 4) */}
      <div className={styles.cellNote}>
        <input
          className={styles.noteInput}
          type="text"
          placeholder={dbEntry && dbEntry[4] ? dbEntry[4] : 'Note…'}
          value={row.note || ''}
          onChange={e => onUpdate(row.id, 'note', e.target.value)}
          title={rangeStr}
        />
      </div>

      {/* Remove row */}
      <div className={styles.cellAction}>
        {canRemove && (
          <button
            className={styles.removeBtn}
            onClick={() => onRemove(row.id)}
            title="Remove row"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
